import { UpdateRecordingSink } from "@core-services/portal"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormControl, FormControlLabel, Switch, TextField } from "@vindral/components"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { RouterOutputs } from "../../..//trpc"
import { FormSection } from "../../../atoms/FormSection"
import { SaveButton } from "../../../atoms/SaveButton"
import { SaveSection } from "../../../atoms/SaveSection"
import { Message, MessageSection } from "../../../molecules/Message"
import { RouteBlockDialog } from "../../../molecules/RouteBlockDialog"
import { integerOrNull } from "../../../utils/numberOrNull"

const resolver = zodResolver(UpdateRecordingSink)

type Recording = RouterOutputs["sink"]["findRecordingSink"] | undefined

export interface ChannelEditDetailsRecordingProps {
  channelId: string
  recordingSink: Recording
  canEdit: boolean
  recordingEnabled: boolean
  onSave: (recording: UpdateRecordingSink) => Promise<void>
}

export function ChannelEditDetailsRecording({
  recordingSink,
  channelId,
  canEdit,
  recordingEnabled,
  onSave,
}: ChannelEditDetailsRecordingProps) {
  const { register, handleSubmit, setValue, formState, reset } = useForm({
    resolver,
    mode: "onChange",
    defaultValues: {
      enabled: recordingSink?.enabled ?? false,
      retentionDays: recordingSink?.retentionDays ?? 2,
      channelId,
    },
  })

  const { errors, isDirty, isValid, isSubmitting, isSubmitSuccessful } = formState

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({}, { keepValues: true })
    }
  }, [isSubmitSuccessful, reset])

  const submit = async (recordingSink: UpdateRecordingSink) => {
    const data = {
      ...recordingSink,
    }
    await onSave(data)
  }

  const disabledForm = !canEdit || !recordingEnabled

  return (
    <div className="space-y-4 py-4">
      <RouteBlockDialog condition={isDirty && !isSubmitting} />
      {!recordingEnabled ? (
        <Message severity="warning">
          <MessageSection>
            <div className="flex w-full">
              <div className="grow">
                <span>
                  Recording is not enabled for your organization. Please contact support if you wish to enable recording
                </span>
              </div>
            </div>
          </MessageSection>
        </Message>
      ) : null}

      <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-8">
        <FormSection
          title="Recording"
          description={
            <div>
              <p>
                Toggle the switch to enable or disable recording of the live stream.{" "}
                <span className="text-fg">This feature may be subject to additional fees.</span>
              </p>
              <p className="mt-4">
                Enter the number of days you want the recording to be saved for in the input field provided. After the
                specified number of days, the recording will be automatically deleted.
              </p>
              <p className="mt-4">
                Please note that updating the recording settings will not affect the current live stream.
              </p>
            </div>
          }
          docsLink="https://docs.vindral.com/broadcast/recording"
        >
          <div className="flex flex-col gap-4">
            <FormControl>
              <FormControlLabel error={errors.enabled?.message}>Enable recording</FormControlLabel>
              <Switch
                defaultChecked={recordingSink?.enabled}
                disabled={disabledForm}
                onCheckedChange={(value) => {
                  setValue("enabled", value, { shouldDirty: true })
                }}
                label=""
              />
            </FormControl>

            <FormControl>
              <FormControlLabel error={errors.retentionDays?.message}>Retention (days)</FormControlLabel>
              <TextField
                {...register("retentionDays", { setValueAs: integerOrNull })}
                type="number"
                error={!!errors.retentionDays}
                disabled={disabledForm}
              />
            </FormControl>
          </div>
        </FormSection>

        <SaveSection>
          <SaveButton text="Save" disabled={disabledForm || !isDirty || !isValid} />
        </SaveSection>
      </form>
    </div>
  )
}
